import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Loader2, Trophy, Flame, Sparkles, UserPlus, EyeOff, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

/**
 * FriendLeaderboard — Iter76 / P2.
 *
 * Surfaces a tiny competitive rail: { me } ∪ { users I referred } ∪
 * { users who referred me }, ranked by XP descending. The user's own
 * row is highlighted regardless of position.
 *
 * Self-hides when the user has no friends yet — instead we show a
 * one-tap "Invite friends" CTA pointing at /referrals.
 */
const RANK_COLORS = {
  1: "from-[#FFD700] to-[#FFB067]",
  2: "from-[#d6d6e0] to-[#9fa3b3]",
  3: "from-[#d18a55] to-[#a55b2a]",
};

export default function FriendLeaderboard() {
  const [rows, setRows] = useState(null);
  const [meId, setMeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [optingOut, setOptingOut] = useState(false);
  const [optedOut, setOptedOut] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leaderboard/friends");
      setRows(data?.rows || []);
      setMeId(data?.me_id || null);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const toggleOptOut = async () => {
    setOptingOut(true);
    try {
      const next = !optedOut;
      await api.post("/leaderboard/opt", { opt_out: next });
      setOptedOut(next);
      toast.success(next ? "Hidden from friends' boards" : "Visible to your friends again");
      refresh();
    } catch {
      toast.error("Couldn't update — try again.");
    } finally {
      setOptingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="glass p-5 flex items-center gap-2 text-white/55 text-xs" data-testid="leaderboard-loading">
        <Loader2 size={14} className="animate-spin" /> Loading friends…
      </div>
    );
  }

  // Only self present = no friends yet. Render an invite CTA instead.
  const realFriendCount = (rows || []).filter((r) => !r.is_me).length;
  if (realFriendCount === 0) {
    return (
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="glass p-5 sm:p-6 relative overflow-hidden"
        data-testid="leaderboard-empty"
      >
        <div className="absolute -top-12 -left-12 h-44 w-44 rounded-full bg-[#FFD700]/8 blur-3xl pointer-events-none" aria-hidden />
        <div className="relative flex items-start gap-4">
          <div className="h-11 w-11 grid place-items-center rounded-xl bg-[#FFD700]/12 border border-[#FFD700]/30 shrink-0">
            <Trophy size={18} className="text-[#FFD700]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow text-[#FFD700] mb-1">Friend leaderboard</div>
            <h3 className="font-display text-lg sm:text-xl text-white tracking-tight leading-snug">
              Climb the ranks with friends
            </h3>
            <p className="text-[12.5px] text-white/55 mt-1 leading-relaxed">
              Invite a friend with your referral code — once they sign up you'll both appear here, ranked by XP. <span className="text-white">+5 credits</span> for each successful referral.
            </p>
            <Link
              to="/referrals"
              className="mt-3 inline-flex items-center gap-1.5 text-[12px] btn-primary px-4 py-2 rounded-full font-display font-semibold"
              data-testid="leaderboard-invite-cta"
            >
              <UserPlus size={12} /> Invite friends
            </Link>
          </div>
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="glass p-5 sm:p-6 relative overflow-hidden"
      data-testid="leaderboard-card"
    >
      <div className="absolute -top-12 -left-12 h-44 w-44 rounded-full bg-[#FFD700]/8 blur-3xl pointer-events-none" aria-hidden />
      <div className="relative">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 grid place-items-center rounded-xl bg-[#FFD700]/12 border border-[#FFD700]/30 shrink-0">
              <Trophy size={16} className="text-[#FFD700]" />
            </div>
            <div>
              <div className="label-eyebrow text-[#FFD700]">Friend leaderboard</div>
              <h3 className="font-display text-base sm:text-lg text-white tracking-tight leading-snug">
                {realFriendCount} friend{realFriendCount === 1 ? "" : "s"} climbing with you
              </h3>
            </div>
          </div>
          <button
            type="button"
            onClick={toggleOptOut}
            disabled={optingOut}
            className="text-[10.5px] text-white/45 hover:text-white/85 px-2 py-1 rounded-md hover:bg-white/5 inline-flex items-center gap-1 transition disabled:opacity-50"
            title={optedOut ? "Make me visible again" : "Hide me from friends' boards"}
            data-testid="leaderboard-optout"
          >
            {optedOut ? <Eye size={11} /> : <EyeOff size={11} />}
            {optedOut ? "Show me" : "Hide me"}
          </button>
        </div>

        <ol className="space-y-1.5" data-testid="leaderboard-rows">
          <AnimatePresence initial={false}>
            {(rows || []).map((r, idx) => {
              const rankClass = RANK_COLORS[r.rank];
              return (
                <motion.li
                  key={r.user_id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition ${
                    r.is_me
                      ? "border-[#00FF9D]/35 bg-[#00FF9D]/[0.04]"
                      : "border-white/8 bg-white/[0.02] hover:bg-white/[0.04]"
                  }`}
                  data-testid={`leaderboard-row-${r.user_id}`}
                >
                  {/* Rank */}
                  <div className="w-7 shrink-0 text-center">
                    {rankClass ? (
                      <div className={`h-6 w-6 mx-auto rounded-full bg-gradient-to-br ${rankClass} grid place-items-center text-[10px] font-display font-bold text-[#1a1407] shadow`}>
                        {r.rank}
                      </div>
                    ) : (
                      <div className="text-[11px] text-white/40 font-mono-oak">#{r.rank}</div>
                    )}
                  </div>
                  {/* Avatar / initials */}
                  <div className="h-8 w-8 rounded-full overflow-hidden border border-white/10 shrink-0 grid place-items-center bg-white/[0.06] text-[10px] font-display font-semibold text-white/85">
                    {r.picture ? (
                      <img src={r.picture} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <span>{r.initials}</span>
                    )}
                  </div>
                  {/* Name + level */}
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-display text-white truncate flex items-center gap-1.5">
                      {r.display_name}
                      {r.is_me && (
                        <span className="text-[9.5px] uppercase tracking-[0.18em] text-[#9bffcc] font-mono-oak">you</span>
                      )}
                    </div>
                    <div className="text-[10px] text-white/45 font-mono-oak mt-0.5 truncate">
                      L{r.level} · {r.title}
                    </div>
                  </div>
                  {/* Streak + XP */}
                  <div className="flex flex-col items-end gap-0.5 shrink-0">
                    <div className="text-[12px] font-display font-semibold text-white tabular-nums">
                      {r.xp.toLocaleString()} <span className="text-[9px] text-white/40 font-mono-oak">XP</span>
                    </div>
                    {r.current_streak > 0 ? (
                      <div className="text-[9.5px] text-[#ffb067] font-mono-oak inline-flex items-center gap-0.5">
                        <Flame size={9} /> {r.current_streak}d
                      </div>
                    ) : (
                      <div className="text-[9.5px] text-white/30 font-mono-oak">—</div>
                    )}
                  </div>
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ol>

        <Link
          to="/referrals"
          className="mt-3 inline-flex items-center gap-1 text-[11px] text-white/55 hover:text-white transition"
          data-testid="leaderboard-invite-more"
        >
          <Sparkles size={10} /> Invite more friends &rarr;
        </Link>
      </div>
    </motion.section>
  );
}
